import React, { useEffect, useState } from "react";
import { Box, Fade, Typography } from "@mui/material";
import { useAppSelector } from "../../Store/hooks";
import { selectIsLoading } from "../../Store/loaderSlice";

const SHOW_DELAY_MS = 150;

const MESSAGES = [
  "Routing your journey...",
  "Preparing your trip...",
  "Loading bus schedule...",
  "Fetching route details...",
];

const GlobalLoader: React.FC = () => {
  const isLoading = useAppSelector(selectIsLoading);
  const [visible, setVisible] = useState(false);
  const [msgIndex, setMsgIndex] = useState(0);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (isLoading) {
      timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    } else {
      setVisible(false);
    }
    return () => clearTimeout(timer);
  }, [isLoading]);

  useEffect(() => {
    if (!visible) return;
    const interval = setInterval(() => {
      setMsgIndex((i) => (i + 1) % MESSAGES.length);
    }, 2200);
    return () => clearInterval(interval);
  }, [visible]);

  return (
    <Fade in={visible} timeout={{ enter: 200, exit: 300 }} unmountOnExit>
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: (theme) => theme.zIndex.modal + 1000, // above MUI Dialog (1300) and Drawer (1200)
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(6px)",
          backgroundColor: "rgba(13, 27, 42, 0.72)",
          userSelect: "none",
          pointerEvents: "all",
          overflow: "hidden",
        }}
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        {/* Card */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            px: 5,
            py: 4,
            borderRadius: 4,
            background: "rgba(255,255,255,0.06)",
            backdropFilter: "blur(14px)",
            border: "1px solid rgba(255,255,255,0.12)",
            boxShadow: "0 12px 48px rgba(0,0,0,0.5)",
            minWidth: 260,
          }}
        >
          {/* Logo */}
          <Box
            component="img"
            src="/logo.png"
            alt="TJ Bus"
            sx={{
              height: 44,
              mb: 3,
              objectFit: "contain",
              filter: "brightness(0) invert(1)",
              opacity: 0.9,
            }}
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />

          {/* Road + Bus scene */}
          <Box sx={{ position: "relative", width: 200, mb: 1 }}>
            {/* Bus emoji animating across */}
            <Box
              sx={{
                fontSize: 36,
                lineHeight: 1,
                textAlign: "center",
                animation: "tjbus-drive 1.6s ease-in-out infinite alternate",
                "@keyframes tjbus-drive": {
                  "0%": { transform: "translateX(-30px)" },
                  "100%": { transform: "translateX(30px)" },
                },
              }}
            >
              🚌
            </Box>

            {/* Road track */}
            <Box
              sx={{
                width: "100%",
                height: 6,
                borderRadius: 3,
                background: "rgba(255,255,255,0.12)",
                overflow: "hidden",
                position: "relative",
                mt: "2px",
              }}
            >
              {/* Moving road stripe */}
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: "45%",
                  height: 2,
                  borderRadius: 1,
                  background:
                    "linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)",
                  animation: "tjbus-road 1s linear infinite",
                  "@keyframes tjbus-road": {
                    "0%": { left: "-45%" },
                    "100%": { left: "100%" },
                  },
                }}
              />
            </Box>
          </Box>

          {/* Rotating message */}
          <Typography
            key={msgIndex}
            variant="body2"
            sx={{
              mt: 2.5,
              color: "rgba(255,255,255,0.82)",
              fontWeight: 500,
              letterSpacing: "0.06em",
              fontSize: "0.78rem",
              textTransform: "uppercase",
              animation: "tjbus-fadein 0.4s ease",
              "@keyframes tjbus-fadein": {
                from: { opacity: 0, transform: "translateY(4px)" },
                to: { opacity: 1, transform: "translateY(0)" },
              },
            }}
          >
            {MESSAGES[msgIndex]}
          </Typography>

          {/* Dot pulse indicator */}
          <Box
            sx={{
              display: "flex",
              gap: "6px",
              mt: 2,
            }}
          >
            {[0, 1, 2].map((i) => (
              <Box
                key={i}
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.7)",
                  animation: `tjbus-pulse 1.2s ease-in-out ${i * 0.2}s infinite`,
                  "@keyframes tjbus-pulse": {
                    "0%, 100%": { opacity: 0.25, transform: "scale(0.8)" },
                    "50%": { opacity: 1, transform: "scale(1.2)" },
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Fade>
  );
};

export default GlobalLoader;
